import React from 'react';
import { motion } from 'framer-motion';
import { FIELD_R, FOLLICLE_SLOTS, follicleRadius, REST_RADIUS, STROMA_DOTS } from '../utils/follicleField';
type FollicleFieldProps = {
  n: number | null;
  variant: 'choice' | 'individualized';
  runKey: number;
  reduced: boolean;
  caption: string;
  detail: string;
  emptyLabel: string;
};

// Histology-inspired palette (H&E stain): rose stroma, plum nuclei, clear antrum.
const STROMA = '#f3dbe0';
const STROMA_EDGE = '#dcb7c0';
const GRANULOSA = '#e7bfca';
const STAIN = '#7b4c84';
const ACCENT = '#2448c4';
const OUTER = FIELD_R + 6;
const EASE = [0.23, 1, 0.32, 1] as const;
export function FollicleField({
  n,
  variant,
  runKey,
  reduced,
  caption,
  detail,
  emptyLabel
}: FollicleFieldProps) {
  const empty = n === null;
  const ring = variant === 'individualized' ? ACCENT : '#1c1d22';
  return <figure data-id="element-123" data-mp="g9pfzrxvf701">
      <svg viewBox={`${-OUTER - 10} ${-OUTER - 10} ${(OUTER + 10) * 2} ${(OUTER + 10) * 2}`} className="mx-auto block w-full max-w-[240px]" role="img" aria-label={empty ? `${caption}: ${emptyLabel}` : `${caption}: illustrative follicle response visualization`} data-id="element-124" data-mp="ga8x56fp5rd0">
        <circle r={OUTER + 5} fill="none" stroke={empty ? '#d8d2c6' : ring} strokeWidth={1} strokeDasharray={empty ? '3 5' : undefined} data-id="element-125" data-mp="19yg88gppj1rd" />
        <circle r={OUTER} fill={empty ? '#faf8f4' : STROMA} stroke={empty ? 'none' : STROMA_EDGE} strokeWidth={1} data-id="element-126" data-mp="104fx9y7qv2aq" />

        {empty ? <text textAnchor="middle" y={4} fontFamily="Inter, sans-serif" fontSize={12} fill="#8a8780" data-id="element-127" data-mp="r6dzrsn7yrs7">
            {emptyLabel}
          </text> : <>
            <g fill={STAIN} fillOpacity={0.4} data-id="element-128" data-mp="h7y4rel2mydf">
              {STROMA_DOTS.map((d, i) => <circle key={i} cx={d.x} cy={d.y} r={d.r + 0.3} data-id="element-129" data-mp="26muwrfh65zqc" data-mpi={i} />)}
            </g>
            {FOLLICLE_SLOTS.map((s, i) => {
          const r = follicleRadius(i, n);
          const grown = r > REST_RADIUS + 2;
          const antrum = r > 7 ? r * 0.64 : 0;
          const oocyte = r > 7 ? Math.max(1.6, r * 0.15) : 0;
          const delay = reduced ? 0 : Math.min(i, 26) * 0.025;
          const t = reduced ? {
            duration: 0
          } : {
            duration: 0.28,
            delay,
            ease: EASE
          };
          return <g key={`${runKey}-${i}`} transform={`translate(${s.x.toFixed(1)} ${s.y.toFixed(1)})`} data-id="element-130" data-mp="1w62hhi0f93g7" data-mpi={i}>
                  <motion.circle initial={{
              r: REST_RADIUS
            }} animate={{
              r
            }} transition={t} fill={grown ? GRANULOSA : STROMA} stroke={STAIN} strokeOpacity={grown ? 0.7 : 0.55} strokeWidth={grown ? 1 : 0.8} data-id="element-131" data-mp="2su8g9oanx1mk" />
                  <motion.circle initial={{
              r: 0
            }} animate={{
              r: antrum
            }} transition={t} fill="#ffffff" fillOpacity={0.92} data-id="element-132" data-mp="3blj6024nj8cv" />
                  <motion.circle initial={{
              r: 0,
              cx: 0,
              cy: 0
            }} animate={{
              r: oocyte,
              cx: r * 0.3,
              cy: -r * 0.2
            }} transition={t} fill={STAIN} fillOpacity={0.85} data-id="element-133" data-mp="36u00w7vopg9j" />
                </g>;
        })}
          </>}
      </svg>
      <figcaption className="mt-3 text-center" data-id="element-134" data-mp="17c6av8nevgwp">
        <p className={`text-sm font-medium ${variant === 'individualized' && !empty ? 'text-accent' : 'text-ink'}`} data-id="element-135" data-mp="17czfa2ywdubo">{caption}</p>
        <p className="mt-0.5 font-mono text-xs tabular-nums text-graphite/80" data-id="element-136" data-mp="1c96hbko6aloc">{detail}</p>
      </figcaption>
    </figure>;
}